import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import TeamDisplay from "@/components/TeamDisplay.jsx";

// Welcome dédié au latecomer : le match est aux tirs au but, on l'invite à rejoindre la séance
// (pas le prono fin de match, déjà décidé). Même design plein écran que StepWelcome, recentré sur
// le lot pénos + l'accès direct au mini-jeu.
export const StepWelcomePenalty = ({onStart, contestData}) => {
    const {t} = useTranslation();
    const coverUrl = contestData?.coverUrl;

    const rewardLabel = (contestData?.participantUi?.penaltyRewardLabel || t('welcome.default_reward')).toUpperCase();
    const barNameRaw = contestData?.place?.name;
    const barName = barNameRaw?.toUpperCase();

    const homeTeam = contestData?.fixture?.homeTeam;
    const awayTeam = contestData?.fixture?.awayTeam;
    const hasMatchInfo = homeTeam && awayTeam;

    const [isReady, setIsReady] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setIsReady(true), 400);
        return () => clearTimeout(timer);
    }, []);

    const logoProps = {
        w: "w-12 min-[380px]:w-20",
        h: "h-12 min-[380px]:h-20",
        flagW: "w-14 min-[380px]:w-20",
        flagH: "h-10 min-[380px]:h-14",
        textSize: "text-sm min-[380px]:text-md",
    };

    return (
        <div className="fixed inset-0 z-50 bg-brand-dark font-sans overflow-hidden select-none">
            <style>{`
                :root { --ease-out: cubic-bezier(0.16, 1, 0.3, 1); }
                .anim-bg { transform: scale(1.15); transition: transform 10s ease-out; }
                .anim-bg-active { transform: scale(1); }
                .slide-down { animation: slideDown 0.8s var(--ease-out) forwards; opacity: 0; transform: translateY(-20px); }
                @keyframes slideDown { to { opacity: 1; transform: translateY(0); } }
                .slide-up { animation: slideUp 0.8s var(--ease-out) forwards; opacity: 0; transform: translateY(30px); }
                @keyframes slideUp { to { opacity: 1; transform: translateY(0); } }
                .zoom-in { animation: zoomIn 0.6s var(--ease-out) forwards; opacity: 0; transform: scale(0.8); }
                @keyframes zoomIn { to { opacity: 1; transform: scale(1); } }
                .delay-1 { animation-delay: 0.1s; }
                .delay-2 { animation-delay: 0.2s; }
                .delay-3 { animation-delay: 0.3s; }
                .delay-4 { animation-delay: 0.4s; }
            `}</style>

            {/* --- 1. IMAGE DE FOND --- */}
            <div
                className={`absolute inset-0 z-0 bg-brand-dark transition-opacity duration-1000 ${isReady ? 'opacity-100' : 'opacity-0'}`}>
                {coverUrl && (
                    <>
                        <img
                            src={coverUrl}
                            alt="Cover"
                            className={`w-full h-full object-cover object-top filter brightness-[0.4] grayscale-20 ${isReady ? 'anim-bg-active' : 'anim-bg'}`}
                        />
                        <div
                            className="absolute inset-0 bg-linear-to-t from-brand-dark via-brand-dark/80 to-transparent"></div>
                    </>
                )}
            </div>

            {/* --- 2. HAUT (logo, bar, badge séance, équipes) --- */}
            <div className="absolute top-0 left-0 right-0 z-20 h-[50%] flex flex-col items-center pointer-events-none">
                {isReady && (
                    <div className="pt-10 flex flex-col items-center gap-3 shrink-0 z-30">
                        <div className="slide-down">
                            <h1 className="text-4xl font-black text-white tracking-tight leading-none drop-shadow-md opacity-80">
                                GOJO<span className="text-yellow-400">.</span>
                            </h1>
                        </div>
                        {barName && (
                            <div
                                className="slide-down delay-1 flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/5 backdrop-blur-sm border border-white/10 shadow-sm">
                                <svg className="w-3 h-3 text-action-green" fill="currentColor" viewBox="0 0 24 24">
                                    <path
                                        d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                                </svg>
                                <span
                                    className="text-[10px] font-bold text-gray-300 tracking-widest uppercase truncate max-w-50">
                                    {barName}
                                </span>
                            </div>
                        )}
                        {/* Badge séance EN DIRECT — la spécificité du parcours latecomer */}
                        <div className="slide-down delay-1 flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-500/15 border border-red-400/30">
                            <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"/>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"/>
                            </span>
                            <span className="text-[10px] font-black text-red-300 tracking-widest uppercase">
                                ⚽ {t('penalty.live_badge', 'Tirs au but')}
                            </span>
                        </div>
                    </div>
                )}

                <div className="flex-1 flex flex-col items-center justify-center w-full pb-8">
                    {hasMatchInfo && isReady && (
                        <div className="zoom-in delay-2 flex items-center justify-center gap-6 w-full px-4">
                            <TeamDisplay
                                {...logoProps}
                                team={homeTeam}
                                textColor="text-white drop-shadow-md"
                            />
                            <div className="flex flex-col items-center pb-6">
                                <span
                                    className="text-yellow-400 text-3xl font-black italic tracking-tighter drop-shadow-[0_0_10px_rgba(250,204,21,0.5)]">
                                    VS
                                </span>
                            </div>
                            <TeamDisplay
                                {...logoProps}
                                team={awayTeam}
                                textColor="text-white drop-shadow-md"
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* --- 3. BAS (titre, lot, objectif, CTA) --- */}
            <div className="absolute bottom-0 left-0 right-0 z-10 p-6 pb-10 flex flex-col justify-end min-h-[50%]">
                {isReady && (
                    <>
                        <h2 className="text-3xl min-[380px]:text-4xl font-black text-white uppercase leading-[0.9] tracking-tighter mb-4 sm:mb-6 slide-up delay-2 whitespace-pre-line">
                            {t('penalty.welcome_title', 'Le match est aux tirs au but !')}
                        </h2>

                        <div className="slide-up delay-3 mb-8">
                            <div className="flex items-center gap-2 mb-2">
                                <span className="text-lg">🏆</span>
                                <p className="text-yellow-400 text-[10px] font-bold uppercase tracking-widest">
                                    {t('penalty.welcome_reward', 'À gagner')}
                                </p>
                            </div>
                            <div
                                className="text-2xl min-[380px]:text-3xl font-black text-white leading-none tracking-tight drop-shadow-lg">
                                {rewardLabel}
                            </div>
                        </div>

                        <div className="slide-up delay-3 mb-8 pl-4 border-l border-white/10">
                            <p className="text-[9px] text-gray-500 font-bold uppercase tracking-widest mb-0.5">
                                {t('penalty.live_badge', 'Tirs au but')}
                            </p>
                            <p className="text-sm font-bold text-gray-200">
                                {t('penalty.welcome_subtitle', 'Pronostique chaque tir — but ou raté — et grimpe au classement de la séance.')}
                            </p>
                        </div>

                        <button
                            onClick={onStart}
                            className="w-full py-4 bg-action-green text-white font-black text-md min-[380px]:text-lg rounded-xl shadow-[0_10px_30px_-10px_rgba(16,185,129,0.5)] slide-up delay-4 hover:scale-[1.02] active:scale-98 transition-all uppercase tracking-wide"
                        >
                            {t('penalty.welcome_cta', 'Je joue les tirs au but')} ⚽
                        </button>

                        <p className="text-center text-[9px] text-white/20 mt-4 slide-up delay-4">
                            {t('welcome.free_label', {barName: barNameRaw || t('welcome.house')})}
                        </p>
                    </>
                )}
            </div>
        </div>
    );
};
